/**
 * AuthGuard.tsx
 * 
 * Componente di protezione globale delle rotte.
 * Consente l'accesso pubblico alla homepage istituzionale e alle pagine
 * informative/legali, mentre richiede l'autenticazione per dashboard,
 * aree operative e pannello di amministrazione.
 * Introduce il "casello" per la visualizzazione obbligatoria della pagina /benvenuto
 * al primo accesso.
 */

import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { buildRelativeAppUrl, storeLastIntendedRoute } from "@/lib/navigationPersistence";
import { logNavDebug } from "@/lib/navDebugLogger";

/** Percorsi accessibili a tutti senza autenticazione */
const PUBLIC_PATHS = [
  "/auth",
  "/reset-password",
  // "/corsi", // (Forse in futuro andranno tolti anche questi se tutto il portale è chiuso)
  // "/corso", 
  "/privacy",
  "/cookie-policy",
  "/termini",
  "/guida",
];

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checkingWelcome, setCheckingWelcome] = useState(true);

  // Verifica se la rotta corrente rientra tra quelle pubbliche
  const isPublicPath = PUBLIC_PATHS.some((path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  });

  useEffect(() => {
    if (loading) return;

    // 1. Reindirizzamento ad /auth per utenti non loggati su rotte protette
    if (!user && !isPublicPath) {
      const intendedRoute = buildRelativeAppUrl({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      });

      storeLastIntendedRoute(intendedRoute);

      logNavDebug("auth", "AuthGuard redirect → /auth", {
        from: intendedRoute,
        reason: "user-null",
      }, "warn");

      navigate("/auth", { replace: true, state: { from: intendedRoute } });
      setCheckingWelcome(false);
      return;
    }

    // 2. Il "Casello" di Benvenuto
    // Se l'utente è loggato, non è su una rotta pubblica, e non ha mai visto il benvenuto
    if (user && !isPublicPath) {
      const hasSeenWelcome = user.user_metadata?.has_seen_welcome === true;
      
      // Se non l'ha visto e NON è già sulla pagina /benvenuto, forzalo lì
      if (!hasSeenWelcome && location.pathname !== "/benvenuto") {
        logNavDebug("auth", "AuthGuard redirect → /benvenuto", {
          reason: "first-login-welcome",
        }, "info");
        navigate("/benvenuto", { replace: true });
      }
      
    }
    
    setCheckingWelcome(false);

  }, [user, loading, isPublicPath, navigate, location.pathname, location.search, location.hash]);

  // Durante la verifica della sessione e del casello mostra lo spinner
  if (loading || checkingWelcome) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Se l'utente non è autenticato e la pagina è privata, il rendering
  // è bloccato in attesa dell'effetto di reindirizzamento
  if (!user && !isPublicPath) {
    return null;
  }

  return <>{children}</>;
}